import { useState, useEffect } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export function PayByCard({ bookingId, balance, currency = "CAD" }) {
  const [mode, setMode] = useState("custom");
  const [cards, setCards] = useState(2);
  const [amount, setAmount] = useState(balance.toFixed(2));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAmount(balance.toFixed(2));
  }, [balance]);

  useEffect(() => {
    if (mode === "even") setAmount((balance / cards).toFixed(2));
  }, [mode, cards, balance]);

  const pay = async () => {
    const amt = +(+amount).toFixed(2);
    if (!amt || amt <= 0 || amt > balance + 0.01) return toast.error(`Enter an amount between $0.01 and $${balance.toFixed(2)}.`);
    setLoading(true);
    try {
      const { data } = await api.post(`/pay/${bookingId}/checkout`, { origin_url: window.location.origin, amount: amt });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start payment.");
      setLoading(false);
    }
  };

  return (
    <div className="rounded-sm bg-navy border border-gold/20 p-5" data-testid="pay-by-card">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="h-5 w-5 text-gold" />
        <h3 className="font-heading text-base font-bold text-white">Pay by card</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">You can pay it all on one card, or split it across several cards — pay a portion now and the rest on another card afterwards.</p>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <button type="button" onClick={() => setMode("custom")} data-testid="paycard-mode-custom"
          className={`rounded-sm border px-3 py-2 text-sm font-medium transition-colors duration-200 ${mode === "custom" ? "border-gold bg-gold/10 text-gold" : "border-gold/20 text-slate-300 hover:border-gold/50"}`}>Custom amount</button>
        <button type="button" onClick={() => setMode("even")} data-testid="paycard-mode-even"
          className={`rounded-sm border px-3 py-2 text-sm font-medium transition-colors duration-200 ${mode === "even" ? "border-gold bg-gold/10 text-gold" : "border-gold/20 text-slate-300 hover:border-gold/50"}`}>Split evenly</button>
      </div>

      {mode === "even" && (
        <div className="mb-3">
          <label className="block text-xs text-slate-400 mb-1.5">Number of cards</label>
          <div className="grid grid-cols-5 gap-2">
            {[2, 3, 4, 5, 6].map((n) => (
              <button type="button" key={n} onClick={() => setCards(n)} data-testid={`paycard-cards-${n}`}
                className={`rounded-sm border py-2 text-sm font-medium transition-colors duration-200 ${cards === n ? "border-gold bg-gold/10 text-gold" : "border-gold/20 text-slate-300 hover:border-gold/50"}`}>{n}</button>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">Each card ≈ ${(balance / cards).toFixed(2)} — pay one now, repeat for the rest.</p>
        </div>
      )}

      <label className="block text-xs text-slate-400 mb-1.5">Amount to pay on this card</label>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
          <input type="number" min="0" step="0.01" max={balance} value={amount} onChange={(e) => { setMode("custom"); setAmount(e.target.value); }} data-testid="paycard-amount"
            className="w-full rounded-sm bg-input border border-gold/20 pl-7 pr-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-gold transition-shadow duration-200" />
        </div>
        <span className="text-xs text-slate-500 shrink-0">of ${balance.toFixed(2)}</span>
      </div>

      <button type="button" onClick={pay} disabled={loading} data-testid="paycard-submit"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-60">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : <><CreditCard className="h-5 w-5" /> Pay ${(+amount || 0).toFixed(2)} by card</>}
      </button>
    </div>
  );
}
